import {
  ScheduleFrequencies,
  ScheduleEntryStatuses,
  allScheduledEntriesForAdminQuery,
} from '@aims/shared/report-schedules/constants';
import { EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import Column from 'antd/lib/table/Column';
import dayjs from 'dayjs';
import React, { useCallback, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import settings from '../../settings';

const { Text } = Typography;

const STATUS_COLORS = {
  UPCOMING: 'blue',
  DUE: 'orange',
  OVERDUE: 'red',
  SUBMITTED: 'gold',
  APPROVED: 'green',
  SKIPPED: 'default',
};

const STATUS_LABELS = {
  UPCOMING: 'SCHEDULED',
  DUE: 'DUE',
  OVERDUE: 'OVERDUE',
  SUBMITTED: 'SUBMITTED',
  APPROVED: 'APPROVED',
  SKIPPED: 'SKIPPED',
};

function ScheduledHolisticProjectReportsTab() {
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: settings.pageSize,
  });
  const [localFilters, setLocalFilters] = useState({
    search: '',
    frequency: undefined,
    statuses: undefined,
  });

  const { error, loading, data, refetch } = useQuery(
    allScheduledEntriesForAdminQuery,
    { fetchPolicy: 'network-only' },
  );

  const allEntries = useMemo(
    () => data?.allScheduledEntriesForAdmin || [],
    [data],
  );

  const filteredEntries = useMemo(() => {
    const { search, frequency, statuses } = localFilters;
    const searchLower = (search || '').trim().toLowerCase();
    return allEntries.filter((entry) => {
      if (searchLower) {
        const projectName = (
          entry.holisticProject?.projectName || ''
        ).toLowerCase();
        const projectNo = (
          entry.holisticProject?.projectNo ||
          entry.holisticProject?.projectNumber ||
          ''
        ).toLowerCase();
        const label = (entry.label || '').toLowerCase();
        if (
          !projectName.includes(searchLower) &&
          !projectNo.includes(searchLower) &&
          !label.includes(searchLower)
        ) {
          return false;
        }
      }
      if (frequency && entry.frequency !== frequency) return false;
      if (statuses && statuses.length && !statuses.includes(entry.status))
        return false;
      return true;
    });
  }, [allEntries, localFilters]);

  const onFiltersChanged = useCallback((changed) => {
    setLocalFilters((prev) => ({
      ...prev,
      ...Object.entries(changed).reduce((acc, [k, v]) => {
        acc[k] = Array.isArray(v) ? (v.length ? v : undefined) : v || undefined;
        return acc;
      }, {}),
    }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, []);

  const handleTableChange = useCallback((paginationState) => {
    setPagination({ ...paginationState });
  }, []);

  return (
    <>
      {error && (
        <div style={{ marginBottom: 16 }}>
          <Text type="danger">{error.message}</Text>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Form layout="inline" form={form} onValuesChange={onFiltersChanged}>
          <Form.Item name="search">
            <Input
              placeholder="Search reports"
              suffix={<SearchOutlined />}
              style={{ minWidth: 250 }}
            />
          </Form.Item>
          <Form.Item name="frequency">
            <Select
              placeholder="Frequency"
              allowClear
              style={{ minWidth: 130 }}
            >
              {Object.values(ScheduleFrequencies).map((c) => (
                <Select.Option key={c.key} value={c.key}>
                  {c.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="statuses">
            <Select
              mode="multiple"
              placeholder="Status"
              allowClear
              style={{ minWidth: 160 }}
            >
              {Object.values(ScheduleEntryStatuses).map((s) => (
                <Select.Option key={s.key} value={s.key}>
                  {s.key === 'UPCOMING' ? 'Scheduled' : s.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
        <Button icon={<ReloadOutlined />} onClick={refetch}>
          Refresh
        </Button>
      </div>
      <Table
        dataSource={filteredEntries}
        loading={loading}
        pagination={{ ...pagination, total: filteredEntries.length }}
        onChange={handleTableChange}
        style={{ width: '100%' }}
        rowKey="entryId"
      >
        <Column
          title="Label"
          dataIndex="label"
          key="label"
          render={(label) => label || '-'}
        />
        <Column
          title="Project #"
          dataIndex="holisticProject"
          key="projectNumber"
          width={130}
          render={(holisticProject) =>
            holisticProject?.projectNumber ||
            holisticProject?.projectNo ||
            '-'
          }
        />
        <Column
          title="Project Name"
          dataIndex="holisticProject"
          key="projectName"
          render={(holisticProject) => holisticProject?.projectName || '-'}
        />
        <Column
          title="Frequency"
          dataIndex="frequency"
          key="frequency"
          width={110}
          render={(frequency) => (
            <Tag color={ScheduleFrequencies[frequency]?.color || 'default'}>
              {ScheduleFrequencies[frequency]?.label || frequency}
            </Tag>
          )}
        />
        <Column
          title="Due Date"
          dataIndex="dueDate"
          key="dueDate"
          width={130}
          render={(date) => (date ? dayjs(date).format('MMM D, YYYY') : '-')}
        />
        <Column
          title="Status"
          dataIndex="status"
          key="status"
          width={110}
          render={(status) => (
            <Tag color={STATUS_COLORS[status] || 'default'}>
              {STATUS_LABELS[status] || status || '-'}
            </Tag>
          )}
        />
        <Column
          title="Actions"
          key="actions"
          width={80}
          render={(_, record) =>
            record.scheduledReportId ? (
              <Space>
                <Tooltip title="Review">
                  <Link
                    to={`/scheduled-reports/review/${record.scheduledReportId}?from=/holistic-project-reports&tab=scheduled`}
                  >
                    <Button type="link" icon={<EyeOutlined />} />
                  </Link>
                </Tooltip>
              </Space>
            ) : null
          }
        />
      </Table>
    </>
  );
}

export default ScheduledHolisticProjectReportsTab;
